// ============================================================================
// ShiftTimelineList.jsx
// The page's primary view: one row per shift, drawn on a shared time axis.
//
// The unit is the SHIFT, not the finding. A 32-minute gap at 23:42 means
// something different inside a fourteen-hour shift than inside a six-hour one,
// and a list of gaps can never show that. Meal discrepancies and long
// close-outs are lenses applied to this list rather than the list itself.
//
// Two kinds of row appear, and the difference is visible on purpose:
//
//   * a real shift — punches, yard arrival, inspection, close-out
//   * a meal-only day — gaps on the axis with NO shift envelope, because the
//     post-trip feed has no row for it
//
// The second kind exists because the feeds cover different windows: 62% of
// meal gaps currently fall on days post-trip does not carry. Hiding them would
// drop most of the findings this module exists for, and would read as the data
// improving rather than the view narrowing. They are marked rather than
// dropped, so the coverage gap is visible instead of inferred.
// ============================================================================

import { useMemo } from 'react';
import { c, t, sp, r, chart } from '../../theme';
import { fmtDate, fmtTimePT, splitWorkerName } from './format';
import {
  layoutShift, axisBounds, axisMarks, axisTicks, hhmm, fmtMin, fmtHours, isClearGap,
  visitColor, siteTypeLabel,
} from './timeline';

const who = (row) => splitWorkerName(row.worker_name, row.employee_id);

export default function ShiftTimelineList({
  shifts = [], thresholdMin = 45, preThresholdMin = 60, onSelectSegment, onSelectDriver, selectedKey,
  // Window-wide bounds from /shifts/summary. Absent on the first render and
  // whenever that call fails, which is why axisBounds still knows how to scan.
  axis = null,
}) {
  const laid = useMemo(
    () => shifts.map((s) => ({ key: `${s.employee_id}|${String(s.shift_date).slice(0, 10)}`, row: s, geo: layoutShift(s) }))
                .filter((x) => x.geo),
    [shifts]
  );

  const { axisFrom, axisTo } = useMemo(
    () => axisBounds(laid.map((x) => x.geo), axis), [laid, axis]);
  const span = Math.max(axisTo - axisFrom, 60);
  // Clamped, because the axis is a trimmed band rather than the true extremes:
  // a mark outside it must pin to the edge, not escape the row and overlap the
  // next one. Rows with a pinned mark carry an edge flag so the reader is told
  // the bar is cut rather than reading a shorter shift than the driver worked.
  const pct  = (m) => Math.max(0, Math.min(100, ((m - axisFrom) / span) * 100));
  const ticks = useMemo(() => axisTicks(axisFrom, axisTo), [axisFrom, axisTo]);

  if (!shifts.length) {
    return <div style={s.empty}>No shifts match these filters.</div>;
  }

  return (
    <div>
      <style>{css}</style>

      {/* Sticky so the axis stays readable while scrolling a long day. */}
      <div style={s.axisRow}>
        <div style={s.gutter} />
        <div style={{ position: 'relative', flex: 1, height: 16 }}>
          {ticks.map((m) => (
            <span key={m} style={{ ...s.tick, left: `${pct(m)}%` }}>{hhmm(m)}</span>
          ))}
        </div>
        <div style={s.noteCol} />
      </div>
      <div style={s.axisRule} />

      {laid.map(({ key, row, geo }) => {
        // The axis is a trimmed band, so ~1% of rows have a mark beyond it.
        // Those marks are pinned to the edge by pct(), which without a flag
        // would draw a shift ending exactly at the axis edge -- a shorter day
        // than the driver actually worked, stated with the same confidence as
        // every honest bar on the page.
        const outOfAxis = axisMarks(geo);
        const pinnedLeft  = outOfAxis.some((v) => v < axisFrom);
        const pinnedRight = outOfAxis.some((v) => v > axisTo);
        const over = geo.post_trip?.category === 'measured'
          && Number(geo.post_trip.unaccounted_min) > thresholdMin;
        // The front of the shift has its OWN line (60 by default, against 45
        // here) because the two ends have different shapes — see
        // postTripConfig.PRE_DEFAULT_THRESHOLD_MIN for the distributions.
        const preOver = geo.post_trip?.pre_category === 'measured'
          && Number(geo.post_trip.pre_unaccounted_min) > preThresholdMin;
        const isSel = selectedKey === key;

        return (
          <div key={key} style={{ ...s.row, background: isSel ? c.infoTint : 'transparent' }}>
            <div style={s.gutter}>
              <button
                className="twa-driver"
                style={s.driverBtn}
                onClick={() => onSelectDriver?.(row.employee_id)}
                title={`${who(row).name} · ID ${who(row).id} — show full history`}
              >
                {/* The id sits outside the truncating span so a long name
                    ellipsises without taking the id with it — the id is what
                    disambiguates the four Adrian Gutierrezes in one yard. */}
                <span style={s.driverName}>{who(row).name}</span>
                <span style={s.driverId}>{who(row).id}</span>
              </button>
              <div style={s.gutterMeta}>
                {fmtDate(geo.date)}
                {/* The yard by NAME, with the code after it. "151" alone means
                    nothing to a reader who does not already know the estate by
                    number, and knowing a driver's home base was previously a
                    click into the drill-down. The code stays because it is what
                    people say out loud and what every other board shows. */}
                {row.cost_center_name
                  ? ` · ${row.cost_center_name}${row.cost_center_code ? ` (${row.cost_center_code})` : ''}`
                  : (row.cost_center_code ? ` · ${row.cost_center_code}` : '')}
              </div>
            </div>

            <div style={{ position: 'relative', flex: 1, minHeight: 52 }}>
              {/* Hour grid, behind everything. Rendered first so the marks
                  paint over it, and pointer-events:none so it can never
                  swallow a click meant for a segment — an invisible overlay
                  eating clicks is the classic way a decorative layer breaks a
                  page while looking fine. Same tick positions as the axis
                  labels above, from the same array, so a line can never drift
                  from the hour it claims. */}
              {ticks.map((m) => (
                <span key={`g${m}`} aria-hidden="true"
                      style={{ ...s.gridLine, left: `${pct(m)}%` }} />
              ))}

              {/* Continues past the axis. A caret rather than a colour so it
                  reads as "cut off here" instead of as another category of
                  event competing with the site and meal marks. */}
              {pinnedLeft && (
                <span style={{ ...s.edgeFlag, left: 0 }}
                      title="This shift starts before the axis — the bar is cut here">‹</span>
              )}
              {pinnedRight && (
                <span style={{ ...s.edgeFlag, right: 0 }}
                      title="This shift continues past the axis — the bar is cut here">›</span>
              )}

              {/* worked span */}
              {geo.shiftStart !== null && geo.shiftEnd !== null && (
                <div
                  style={{
                    ...s.bar,
                    left:  `${pct(geo.shiftStart)}%`,
                    width: `${Math.max(pct(geo.shiftEnd) - pct(geo.shiftStart), 0.6)}%`,
                  }}
                >
                  <span style={s.barLabel}>{fmtHours(geo.workedMinutes)}</span>
                </div>
              )}

              {/* A meal-only day: no envelope to draw, so say so rather than
                  leaving a bar-shaped hole the reader has to interpret. */}
              {!geo.hasRecord && (
                <span style={{ ...s.noRecordRule, left: 0, right: 0 }} />
              )}

              {geo.meals.map((m) => {
                const left = pct(m.from);
                const w = m.to !== null ? Math.max(pct(m.to) - left, 0.5) : 0.5;
                const clear = isClearGap(m);
                return (
                  <button
                    key={m.id}
                    className="twa-seg"
                    title={`Meal gap ${fmtTimePT(m.gap_start)} · ${Number(m.gap_minutes)} min`}
                    onClick={(e) => onSelectSegment?.({ kind: 'meal', row, geo, meal: m, key,
                      anchorRect: e.currentTarget.getBoundingClientRect() })}
                    style={{
                      ...s.meal,
                      left: `${left}%`, width: `${w}%`,
                      // Solid only for the tier the source system calls
                      // unambiguous; ambiguous gaps are outlined, so the two
                      // are separable without equal visual weight.
                      background: clear ? c.danger : c.warningTint,
                      border: clear ? 'none' : `1px solid ${c.warning}`,
                      top: geo.hasRecord ? 12 : 14,
                    }}
                  />
                );
              })}

              {/* Pre-trip: punch-in -> yard departure, the mirror of the
                  close-out segment below. Drawn only where the export actually
                  measured this end — 1,055 of 18,811 rows carry a departure —
                  because a segment the reader can click is a claim that
                  something was observed, and an empty one is a lie about
                  coverage the page states honestly everywhere else. */}
              {geo.hasRecord && geo.shiftStart !== null && geo.yardDeparture !== null && (
                <button
                  className="twa-seg"
                  title={`In ${fmtTimePT(geo.post_trip.shift_start)} · left ${fmtTimePT(geo.post_trip.yard_departure)}`}
                  onClick={(e) => onSelectSegment?.({ kind: 'startshift', row, geo, key,
                    anchorRect: e.currentTarget.getBoundingClientRect() })}
                  style={{
                    ...s.startshift,
                    left:  `${pct(geo.shiftStart)}%`,
                    width: `${Math.max(pct(geo.yardDeparture) - pct(geo.shiftStart), 0.8)}%`,
                    // c.brand is Athens RED (#D1232A), not the navy it sounds
                    // like — this read as a finding on every start, measured or
                    // not. Identity now comes from chart.shiftStart; red is
                    // reserved for the over-threshold state at both ends.
                    borderColor: preOver ? c.danger : chart.shiftStart,
                    background:  preOver ? chart.overFill : chart.shiftStartFill,
                  }}
                />
              )}

              {geo.preInspEnd !== null && (
                <span
                  style={{
                    ...s.preInspTick,
                    left: `${Math.max(0, Math.min(pct(geo.preInspEnd), 100))}%`,
                    // Clamped and dimmed rather than dropped, same as the
                    // post-trip tick: a form_left_open row sits hours outside
                    // the axis, and silently omitting it would read as "no
                    // inspection" when the truth is "an implausible one".
                    opacity: geo.preInspEnd < axisFrom || geo.preInspEnd > axisTo ? 0.3 : 1,
                  }}
                />
              )}

              {geo.hasRecord && geo.yardArrival !== null && geo.shiftEnd !== null && (
                <button
                  className="twa-seg"
                  title={`Back ${fmtTimePT(geo.post_trip.yard_arrival)} · out ${fmtTimePT(geo.post_trip.shift_end)}`}
                  onClick={(e) => onSelectSegment?.({ kind: 'endshift', row, geo, key,
                    anchorRect: e.currentTarget.getBoundingClientRect() })}
                  style={{
                    ...s.endshift,
                    left:  `${pct(geo.yardArrival)}%`,
                    width: `${Math.max(pct(geo.shiftEnd) - pct(geo.yardArrival), 0.8)}%`,
                    borderColor: over ? c.danger : chart.shiftEnd,
                    background:  over ? chart.overFill : chart.shiftEndFill,
                  }}
                />
              )}

              {/* A lane with no reading is not a lane with no visits. Upstream
                  decided the note column stays silent for anything not worth
                  acting on, and coverage is not a finding about a person — so
                  the distinction is drawn rather than narrated, the same way a
                  missing shift record is. */}
              {row.has_visit_data === false && (
                <span aria-hidden="true" style={{ ...s.visitLaneNoData, left: 0, right: 0 }}
                      title="No site-visit data has been synced for this day" />
              )}

              {/* Site visits, in their own lane below the shift bar rather than over
                  it. A driver can log seven in a day and they routinely overlap the
                  worked span at both ends; drawn on top they would bury the meal gaps
                  and the close-out block that already share it. */}
              {(geo.visits || []).map((v) => {
                const left = pct(v.from);
                // No `departed` means the producer saw an entry and no exit. Drawn as
                // a narrow point, not a guessed span.
                const w = v.to !== null ? Math.max(pct(v.to) - left, 0.6) : 0.6;
                return (
                  <button
                    key={v.id}
                    className="twa-seg"
                    title={`${v.site || 'Site'} · ${siteTypeLabel(v.site_type)} · ${fmtMin(v.minutes)}`}
                    onClick={(e) => onSelectSegment?.({ kind: 'visit', row, geo, visit: v, key,
                      anchorRect: e.currentTarget.getBoundingClientRect() })}
                    style={{ ...s.visit, left: `${left}%`, width: `${w}%`,
                             background: visitColor(chart, v.site_type) }}
                  />
                );
              })}

              {geo.inspEnd !== null && (
                <span
                  style={{
                    ...s.inspTick,
                    left: `${Math.max(0, Math.min(pct(geo.inspEnd), 100))}%`,
                    opacity: geo.inspEnd < axisFrom || geo.inspEnd > axisTo ? 0.3 : 1,
                  }}
                />
              )}
            </div>

            {/* Findings only.
                This column used to narrate every row — "workday only", "no
                yard arrival", a close-out figure for all 665 measured shifts
                whether or not anything was wrong. Reading down 50 rows, the
                flagged ones did not stand out from the ordinary ones, which is
                the whole job of a column like this. An ordinary shift now says
                nothing at all: its figures are one click away in the popover,
                and the bar already shows the windows.

                What survives is what someone would act on: a window over its
                own threshold, the two "before" findings at either end, and a
                reading the data itself says not to trust. Coverage states
                (workday only, no yard arrival, no shift record) are gone from
                here — the dashed rule already says a day has no record, and a
                blank end already reads as blank on the bar. */}
            <div style={s.noteCol}>
              {preOver && (
                <span style={{ ...s.noteStrong, color: c.dangerText }}>
                  {fmtMin(geo.post_trip.pre_unaccounted_min)} start
                </span>
              )}
              {over && (
                <span style={{ ...s.noteStrong, color: c.dangerText }}>
                  {fmtMin(geo.post_trip.unaccounted_min)} close-out
                </span>
              )}
              {geo.post_trip?.category === 'out_before_return' && (
                <span style={s.noteFlag}>out before return</span>
              )}
              {geo.post_trip?.pre_completeness === 'departure_before_shift' && (
                <span style={s.noteFlag}>departure before shift</span>
              )}
              {/* Data quality, not conduct — so it is stated plainly and
                  dimmed, never in the same ink as a finding about a person. */}
              {(geo.post_trip?.category === 'form_left_open'
                || geo.post_trip?.pre_category === 'form_left_open') && (
                <span style={s.noteDim}>unreliable reading</span>
              )}
              {geo.meals.length > 0 && (
                <span style={s.gapChip}>
                  {geo.meals.length} gap{geo.meals.length === 1 ? '' : 's'}
                  {/* Split by tier, because the page treats them differently
                      everywhere else: one count would hide that all three of a
                      shift's gaps are the ambiguous kind. */}
                  {geo.meals.filter(isClearGap).length > 0
                    && ` · ${geo.meals.filter(isClearGap).length} clear`}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const css = `
  .twa-seg { cursor: pointer; transition: filter .12s ease, transform .12s ease; }
  .twa-seg:hover { filter: brightness(1.12); transform: scaleY(1.15); }
  .twa-seg:focus-visible { outline: 2px solid ${c.info}; outline-offset: 2px; }
  .twa-driver { cursor: pointer; }
  .twa-driver:hover { text-decoration: underline; }
  .twa-driver:focus-visible { outline: 2px solid ${c.info}; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { .twa-seg { transition: none; } }
`;

const btnReset = { padding: 0, margin: 0, font: 'inherit', border: 'none', background: 'none', appearance: 'none' };

const s = {
  axisRow:  { display: 'flex', alignItems: 'flex-end', gap: sp[3], position: 'sticky', top: 0,
              background: c.surface, zIndex: 2, paddingTop: sp[2] },
  axisRule: { height: 1, background: c.border, margin: `4px 0 ${sp[2]} 202px`,
              position: 'sticky', top: 18, zIndex: 2 },
  tick:     { position: 'absolute', transform: 'translateX(-50%)', ...t.micro, color: c.textFaint, ...t.tabular },
  // Recessive by design: the grid orients the eye, it is not data. chart.grid
  // is the same value the Recharts axes use, so the page has one grid colour.
  gridLine: { position: 'absolute', top: 0, bottom: 0, width: 1, background: chart.grid,
              pointerEvents: 'none' },

  row:    { display: 'flex', alignItems: 'center', gap: sp[3], padding: `2px 0`,
            borderBottom: `1px solid ${c.surfaceSunk}` },
  gutter: { width: 190, flexShrink: 0, paddingLeft: sp[1] },
  driverBtn:  { ...btnReset, textAlign: 'left', display: 'flex', alignItems: 'baseline',
                gap: 5, maxWidth: '100%' },
  driverName: { ...t.bodySm, fontWeight: 600, color: c.info,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  driverId:   { ...t.micro, ...t.tabular, color: c.textFaint, flexShrink: 0 },
  gutterMeta: { ...t.micro, color: c.textFaint, ...t.tabular },

  bar: { position: 'absolute', top: 8, height: 22, borderRadius: r.sm,
         background: c.successTint, border: `1px solid ${c.success}33`,
         display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  barLabel: { ...t.micro, color: c.successText, whiteSpace: 'nowrap', ...t.tabular },

  // A dashed rule where the worked bar would be, so a meal-only day reads as
  // "we have no shift for this" rather than as an empty row.
  edgeFlag: { position: 'absolute', top: 8, height: 22, lineHeight: '22px',
              ...t.bodySm, fontWeight: 700, color: c.textFaint,
              padding: '0 2px', cursor: 'default', zIndex: 3 },
  noRecordRule: { position: 'absolute', top: 19, height: 0,
                  borderTop: `1px dashed ${c.borderStrong}`, pointerEvents: 'none' },

  meal:     { ...btnReset, position: 'absolute', height: 16, borderRadius: 2, minWidth: 3 },
  // Its own lane under the bar, and short — seven of them should read as a
  // sequence of stops, not a second set of bars competing with the shift.
  visit:    { ...btnReset, position: 'absolute', top: 36, height: 9, borderRadius: 2,
              minWidth: 3, opacity: .9 },
  visitLaneNoData: { position: 'absolute', top: 40, height: 0,
                     borderTop: `1px dashed ${c.border}`, pointerEvents: 'none' },
  endshift: { ...btnReset, position: 'absolute', top: 4, height: 30, borderRadius: r.sm,
              borderWidth: 1.5, borderStyle: 'solid', minWidth: 4 },
  inspTick: { position: 'absolute', top: 3, height: 32, width: 2, background: chart.shiftEnd,
              borderRadius: 1, pointerEvents: 'none' },
  // Pre-trip mirrors the two above, in the start hue: the two ends are
  // separable by hue as well as by position on the axis.
  startshift: { ...btnReset, position: 'absolute', top: 4, height: 30, borderRadius: r.sm,
                borderWidth: 1.5, borderStyle: 'solid', minWidth: 4 },
  preInspTick: { position: 'absolute', top: 3, height: 32, width: 2, background: chart.shiftStart,
                 borderRadius: 1, pointerEvents: 'none' },

  noteCol:    { width: 132, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0,
                paddingLeft: sp[2] },
  // A finding that has no number of its own. Same weight as the figures above
  // it, so a scan down the column does not rank "52 min start" above "out
  // before return" purely because one is bold.
  noteFlag:   { ...t.micro, fontWeight: 600, color: c.dangerText },
  noteStrong: { ...t.bodySm, fontWeight: 650, ...t.tabular },
  noteDim:    { ...t.micro, color: c.textFaint },
  gapChip:    { ...t.micro, color: c.warningText, fontWeight: 600 },

  empty: { ...t.bodySm, color: c.textMuted, padding: `${sp[8]} 0`, textAlign: 'center' },
};
